import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomerService, DriverService, EmployeeService, EventsService, SignatureRequestService, TransRoutesService } from "../../services";
import { selectAllActiveDrivers, selectAllActiveVehicles } from "./../../store";
import moment from 'moment';
import DatePicker from "react-datepicker";
import { PERSONAL_ROUTE_STATUS, ROUTE_STATUS } from "../../shared";
import { buildExternalDriverNameByIdMap, formatDriverDropdownLabel, sortDriversByLastName } from "../../shared/utils/driverDropdownOptions";

const RouteSignatureList = () => {
  const params = new URLSearchParams(window.location.search);
  const drivers = useSelector(selectAllActiveDrivers);
  const vehicles = useSelector(selectAllActiveVehicles);
  
  const navigate = useNavigate();
  const [dateSelected, setDateSelected] = useState(new Date());
  const [routes, setRoutes] = useState([]);
  const [directorSignature, setDirectorSignature] = useState(undefined);
  const [selectedFile, setSelectedFile] = useState();
  const [selectedDriver, setSelectedDriver] = useState(undefined);
  const [driverList, setDriverList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [externalDriverNameById, setExternalDriverNameById] = useState({});

  const redirectToDashboard = () => {
    navigate(`/trans-routes/dashboard`);
  }

  const uploadDirectorSignature = () => {
    const formData = new FormData();
    const site = EventsService.site;
  	formData.append("file", selectedFile);
    if (selectedFile) {
      if (directorSignature) {
        CustomerService.deleteFile({'name': `center_director_signature_site_${site}`}).then(() => {
          CustomerService.uploadAvatar(`center_director_signature_site_${site}`, formData).then(() => {
            CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
              if (data?.data) {
                setDirectorSignature(data?.data)
              }
            });
          })
        })
      } else {
        CustomerService.uploadAvatar(`center_director_signature_site_${site}`, formData).then(() => {
          CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
            if (data?.data) {
              setDirectorSignature(data?.data)
            }
          });
        })
      }
    }
    
  }

  const getAllUniqueCustomers = (routes) => {
    let result = [];
    for (const route of routes) {
      const customerList = route.route_customer_list.map(item => Object.assign({}, item, {routeType: route.type, routeId: route.id, route: route, customer_status_inbound: route.type === 'inbound' && item.customer_route_status, customer_status_outbound: route.type === 'outbound' && item.customer_route_status,  inbound: route.type === 'inbound' && route, outbound: route.type === 'outbound' && route}))
      for (const customer of customerList) {
        const existItem = result.find((item => (item.customer_id === customer.customer_id) || (item?.customer_name?.replaceAll(' ', '')?.toLowerCase() === customer?.customer_name?.replaceAll(' ', '')?.toLowerCase()) ));
        if (existItem) {
          result = result.filter(item => item !== existItem);
          const newItem = Object.assign({}, existItem, {
            customer_enter_center_time: existItem?.customer_enter_center_time || customer?.customer_enter_center_time,
            customer_leave_center_time: existItem?.customer_leave_center_time || customer?.customer_leave_center_time,
            customer_pickup_time: existItem?.customer_pickup_time || customer?.customer_pickup_time,
            customer_dropoff_time: existItem?.customer_dropoff_time || customer?.customer_dropoff_time,
            inbound: existItem?.inbound || customer?.inbound,
            outbound: existItem?.outbound || customer?.outbound,
            customer_status_inbound: existItem?.customer_status_inbound || customer?.customer_status_inbound,
            customer_status_outbound: existItem?.customer_status_outbound || customer?.customer_status_outbound
          })
          result.push(newItem);
        } else {
          result.push(customer);
        }
      }
    }
    return result.sort((a, b) => {
      if (a.customer_name < b.customer_name) {
        return -1;
      } else {
        return 1;
      }
    });
  }

  useEffect(() => {
    DriverService.getAllActiveDrivers('driver', 'active').then((data) => {
      setDriverList(data.data);
    });
    CustomerService.getAllCustomers().then((data) => setCustomers(data?.data));
  }, []);

  const routesDriverLookupKey = useMemo(() => {
    if (!routes?.length) return '';
    return routes.map((r) => r?.driver ?? '').sort().join('|');
  }, [routes]);

  useEffect(() => {
    if (!routesDriverLookupKey) {
      setExternalDriverNameById({});
      return;
    }
    let cancelled = false;
    EmployeeService.getExternalUserPermissionsList(EventsService.site)
      .then((res) => {
        if (!cancelled) setExternalDriverNameById(buildExternalDriverNameByIdMap(res?.data || []));
      })
      .catch(() => {
        if (!cancelled) setExternalDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, [routesDriverLookupKey]);

  const getRouteDriverDisplayName = (driverId) => {
    if (driverId === undefined || driverId === null || driverId === '') return '';
    const fromFleet = drivers.find((d) => String(d.id) === String(driverId))?.name;
    if (fromFleet) return fromFleet;
    return externalDriverNameById[String(driverId)] || '';
  };

  useEffect(() => {
    const site = EventsService.site;
    const toDateTokens = (dateValue) => {
      const tokenSet = new Set();
      const localFormatted = moment(dateValue)?.format('MM/DD/YYYY');
      const utcFormatted = moment.utc(dateValue)?.format('MM/DD/YYYY');
      [localFormatted, utcFormatted].forEach((formatted) => {
        if (!formatted || formatted.toLowerCase().includes('invalid')) return;
        const [month, day] = formatted.split('/');
        if (month && day) {
          tokenSet.add(`${month}_${day}`);
        }
      });
      return Array.from(tokenSet).map((token) => token.split('_'));
    };
    const resolveRouteSignature = async (routeItem) => {
      const requestRes = await SignatureRequestService.getAllSignatureRequests({ route_id: routeItem?.id }).catch(() => ({ data: [] }));
      const requestList = requestRes?.data || [];
      const driverIds = Array.from(new Set([
        routeItem?.driver,
        ...requestList.map((item) => item?.driver_id)
      ].filter(Boolean)));
      const datePairs = [];
      [routeItem?.schedule_date, dateSelected, ...requestList.map((item) => item?.route_date)].forEach((dateValue) => {
        toDateTokens(dateValue).forEach((pair) => datePairs.push(pair));
      });
      const uniqueDatePairs = Array.from(new Set(datePairs.map((pair) => `${pair[0]}_${pair[1]}`)))
        .map((token) => token.split('_'));
      for (const driverId of driverIds) {
        for (const [month, day] of uniqueDatePairs) {
          try {
            const result = await CustomerService.getAvatar(`${routeItem.id}_${driverId}_${month}_${day}`);
            if (result?.data) {
              return result.data;
            }
          } catch (ex) {
            // Continue trying candidate keys.
          }
        }
      }
      return undefined;
    };

    CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
      if (data?.data) {
        setDirectorSignature(data?.data)
      }
    });
    TransRoutesService.getAll(moment(dateSelected)?.format('MM/DD/YYYY')).then(data => {
      const routesResults = data.data;
      const finalRoutes = routesResults.map(async (routeItem) => {
        const signature = await resolveRouteSignature(routeItem);
        return signature ? Object.assign({}, routeItem, {signature}) : routeItem;
      });
      Promise.all(finalRoutes).then(finalRoutesData => {
        setRoutes(finalRoutesData);
      })
    })
  }, [dateSelected])


  return (
    <>
      <div className="list row mb-4">
        <div className="col-md-12 text-primary">
           <h5>View Routes Signatures <button className="btn btn-link btn-sm" onClick={() => {redirectToDashboard()}}>Back To Dashboard</button></h5> 
        </div>
      </div>
      
      <div className="list row mb-4">
				<div className="col-md-12">
					<div className="mb-4 col-md-4 col-sm-12">
						Select Date to start: <DatePicker selected={dateSelected} onChange={(v) => setDateSelected(v)} shouldCloseOnSelect />
					</div>
          <div className="mb-4 col-md-4 col-sm-12">
						Filter By Driver: <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}>
                <option value={null}></option>
                {sortDriversByLastName(driverList).map((driver) => <option key={driver?.id} value={driver?.id}>{formatDriverDropdownLabel(driver)}</option>)}
              </select>
					</div>
					<table className="personnel-info-table me-4">
            <thead>
              <tr>
                <th>Route Name</th>
                <th>Driver</th>
                <th>Route End Time</th>
                <th>Route Type</th>
								<th>Signature</th>
              </tr>
            </thead>
            <tbody>
							{
								routes && routes.filter((route) => {
                  if (!selectedDriver) {
                    return route;
                  } else {
                    return route?.driver === selectedDriver;
                  }
                })?.map(({id, name, end_time, driver, type, signature}, index) => {
									return (<tr key={index}>
										<td>{name}</td>
										<td>{getRouteDriverDisplayName(driver)}</td>
										<td>{end_time? moment(end_time).format('HH:mm'): ''}</td>
                    <td>{type}</td>
										<td>
                      {/* {images.find((img) => img.id === id)?.image && <img width="100px" src={`data:image/jpg;base64, ${images.find((img) => img.id === id)?.image}`}/>} */}
                      {signature && <img width="100px" src={`data:image/jpg;base64, ${signature}`}/>}
                    </td>
									</tr>)
								})
							}
						</tbody>
					</table>
          <hr></hr>

          {moment(dateSelected)?.format('MM/DD/YYYY')}
          <div style={{'display': 'flex'}}>
            <div>
              <table className="personnel-info-table">
                <thead>
                  <tr>
                    <th>Index</th>
                    <th>Customer Name</th>
                    <th>Pickup Time</th>
                    <th>Enter Center Time</th>
                    <th>Leave Center Time</th>
                    <th>Drop off TIme</th>
                    <th>MA Number</th>
                    <th>Inbound Name</th>
                    <th>Outbound Name</th>
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    getAllUniqueCustomers(routes?.filter((route) => {
                      if (!selectedDriver) {
                        return route;
                      } else {
                        return route?.driver === selectedDriver;
                      }
                    }))?.map(({customer_name, customer_status_inbound, customer_status_outbound, customer_id, customer_enter_center_time, customer_dropoff_time, customer_leave_center_time, customer_pickup_time, inbound, outbound}, index) => {
                      console.log('customers', customers);
                      return (<tr key={index}>
                        <td>{index+1}</td>
                        <td>{customer_name}</td>
                        <td style={{'backgroundColor': `${customer_status_inbound === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT || customer_status_inbound === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? 'red':'white'}`}}>{customer_status_inbound !== PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT &&  customer_status_inbound !== PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT? (customer_pickup_time ? new Date(customer_pickup_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) : '') : ''}</td>
                        <td style={{'backgroundColor': `${customer_status_inbound === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT || customer_status_inbound === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? 'red':'white'}`}}>{customer_status_inbound !== PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT &&  customer_status_inbound !== PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? (customer_enter_center_time ? new Date(customer_enter_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}): ''): ''}</td>
                        <td style={{'backgroundColor': `${customer_status_outbound === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT || customer_status_outbound === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? 'red':'white'}`}}>{customer_status_outbound !== PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT && customer_status_outbound !== PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? (customer_leave_center_time ? new Date(customer_leave_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}): ''): ''}</td>
                        <td style={{'backgroundColor': `${customer_status_outbound === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT || customer_status_outbound === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT ? 'red':'white'}`}}>{customer_status_outbound !== PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT && customer_status_outbound !== PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT? (customer_dropoff_time ? new Date(customer_dropoff_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) : ''): ''}</td>
                        <td>{customers.length > 0 && customers.find(c => c?.id === customer_id || c?.name === customer_name)?.medicaid_number || customers.find(c => c?.id === customer_id)?.medicare_number}</td>
                        <td>{inbound?.name || ''}</td>
                        <td>{outbound?.name || ''}</td>
                        <td>{customer_leave_center_time && customer_enter_center_time &&  Math.round((new Date(customer_leave_center_time) - new Date(customer_enter_center_time))/1000/3600) || ''}</td>
                      </tr>)
                    })
                  }
                </tbody>
              </table>
            </div>
            <div className="ms-4">
                <div className="mb-4"><strong>Center Director Signature:</strong></div>
                {directorSignature && <div className="mb-4"><img width="200px" src={`data:image/jpg;base64, ${directorSignature}`}/></div>}
                {!directorSignature && <div className="mb-4">No Director Signature Uploaded yet</div>}
                <div className="mb-4"><strong>Upload Center Director New Signature: </strong></div>
                <div className="mb-4"><input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} className="form-control-file border"></input></div>
                <div className="mb-4"><button onClick={() => uploadDirectorSignature()} className="btn btn-sm btn-primary">Submit</button></div>
            </div>
          </div>
          
				</div>
			</div>
    </>
    
  );
};

export default RouteSignatureList;